import React from 'react';
import { Card, Checkbox, Dropdown, Tag, Button, Modal } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { UnifiedSim } from '../services/adminService';
import { providerLabel } from '../utils/providerLabel';

/**
 * SimListCard
 * ----------------------------------------------------------------------------
 * Renders a single SIM as a card. Used in narrow viewports as a replacement
 * for the desktop Table row.
 *
 * Mirrors the column structure of the desktop table:
 *   - Top row: checkbox (if selectable), provider tag, identifier (clickable
 *     for VRS), kebab menu
 *   - Body: label/value pairs for MSISDN, Status, Alias, Allocated To,
 *     Assigned To
 *
 * Action handlers are passed in from SimInventory.tsx — the card doesn't own
 * any business logic, just presentation.
 */

const providerColor = (provider: string): string => {
  switch (provider) {
    case 'vrs':
      return 'blue';
    case 'giacom':
      return 'green';
    default:
      return 'default';
  }
};

const statusColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'green';
    case 'suspended':
      return 'orange';
    case 'deactivated':
      return 'red';
    default:
      return 'default';
  }
};

export type SimListCardHandlers = {
  /** Open the allocate modal for this SIM */
  onAllocate: (sim: UnifiedSim) => void;
  /** Open the assign modal for this SIM */
  onAssign: (sim: UnifiedSim) => void;
  /** Unassign (remove asset/user but keep org allocation) */
  onUnassign: (assignmentId: number) => void;
  /** Deallocate (fully remove from org) */
  onDeallocate: (assignmentId: number) => void;
  /** SIM carrier operations (activate/suspend/resume) */
  onSimOperation: (sim: UnifiedSim, operation: 'activate' | 'suspend' | 'resume') => void;
  /** Open the terminate confirmation modal */
  onTerminate: (sim: UnifiedSim) => void;
};

type SimListCardProps = {
  sim: UnifiedSim;
  /** Whether the current user has reseller_admin+ privileges */
  canOperate: boolean;
  /** Whether this card is selected (for bulk actions) */
  selected: boolean;
  /** Whether the checkbox should be disabled (e.g. already allocated) */
  selectDisabled?: boolean;
  /** Called when the selection state changes */
  onSelectChange: (selected: boolean) => void;
  /** Action handlers */
  handlers: SimListCardHandlers;
};

/**
 * Helper for rendering label/value rows in the card body. Keeps the visual
 * treatment consistent (muted label, normal value).
 */
const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 13 }}>
    <span style={{ color: 'rgba(0, 0, 0, 0.55)', minWidth: 100, flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
  </div>
);

const SimListCard: React.FC<SimListCardProps> = ({
  sim,
  canOperate,
  selected,
  selectDisabled = false,
  onSelectChange,
  handlers,
}) => {
  // ------------------------------------------------------------------
  // Build the kebab menu items based on SIM state
  // ------------------------------------------------------------------
  const menuItems: MenuProps['items'] = [];

  // Allocation actions
  if (!sim.allocation) {
    menuItems.push({
      key: 'allocate',
      label: 'Allocate',
      onClick: () => handlers.onAllocate(sim),
    });
  } else if (!sim.allocation.assetId && !sim.allocation.userId) {
    menuItems.push({
      key: 'assign',
      label: 'Assign',
      onClick: () => handlers.onAssign(sim),
    });
    menuItems.push({
      key: 'deallocate',
      label: 'Deallocate',
      danger: true,
      onClick: () =>
        Modal.confirm({
          title: 'Deallocate SIM',
          icon: <ExclamationCircleOutlined />,
          content: 'Remove this SIM from the organization?',
          okText: 'Yes, deallocate',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: () => handlers.onDeallocate(sim.allocation!.assignmentId),
        }),
    });
  } else {
    menuItems.push({
      key: 'unassign',
      label: 'Unassign',
      danger: true,
      onClick: () =>
        Modal.confirm({
          title: 'Unassign SIM',
          icon: <ExclamationCircleOutlined />,
          content: 'Remove asset/user assignment?',
          okText: 'Yes, unassign',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: () => handlers.onUnassign(sim.allocation!.assignmentId),
        }),
    });
  }

  // SIM carrier operations (VRS only, reseller_admin+ only)
  if (canOperate && sim.provider === 'vrs') {
    // Divider before carrier operations to separate from allocation actions
    if (menuItems.length > 0) {
      menuItems.push({ type: 'divider' });
    }

    if (sim.status === 'suspended') {
      menuItems.push({
        key: 'resume',
        label: 'Resume',
        onClick: () =>
          Modal.confirm({
            title: 'Resume SIM',
            icon: <ExclamationCircleOutlined />,
            content: 'Reactivate this suspended SIM?',
            okText: 'Yes, resume',
            cancelText: 'Cancel',
            onOk: () => handlers.onSimOperation(sim, 'resume'),
          }),
      });
    }
    if (sim.status === 'active') {
      menuItems.push({
        key: 'suspend',
        label: 'Suspend',
        onClick: () =>
          Modal.confirm({
            title: 'Suspend SIM',
            icon: <ExclamationCircleOutlined />,
            content: 'Suspend this active SIM?',
            okText: 'Yes, suspend',
            cancelText: 'Cancel',
            onOk: () => handlers.onSimOperation(sim, 'suspend'),
          }),
      });
    }
    if (sim.status !== 'active' && sim.status !== 'suspended') {
      menuItems.push({
        key: 'activate',
        label: 'Activate',
        onClick: () =>
          Modal.confirm({
            title: 'Activate SIM',
            icon: <ExclamationCircleOutlined />,
            content: 'Activate this SIM?',
            okText: 'Yes, activate',
            cancelText: 'Cancel',
            onOk: () => handlers.onSimOperation(sim, 'activate'),
          }),
      });
    }
    menuItems.push({
      key: 'terminate',
      label: 'Terminate',
      danger: true,
      onClick: () => handlers.onTerminate(sim),
    });
  }

  // ------------------------------------------------------------------
  // Build the "Assigned To" display — multiple parts to render
  // ------------------------------------------------------------------
  const renderAssignedTo = (): React.ReactNode => {
    if (!sim.allocation) return '\u2014';
    const parts: React.ReactNode[] = [];

    if (sim.allocation.assetName) {
      parts.push(
        <Link key="asset" to={`/admin/assets/${sim.allocation.assetId}`}>
          {sim.allocation.assetName}
        </Link>
      );
    }
    if (sim.allocation.userName) {
      parts.push(<span key="user-sep">{parts.length > 0 ? ' / ' : ''}</span>);
      parts.push(
        <Link key="user" to={`/admin/users/${sim.allocation.userId}`}>
          {sim.allocation.userName}
        </Link>
      );
    }
    if (sim.allocation.simType) {
      parts.push(
        <Tag key="type" color="purple" style={{ marginLeft: 4 }}>
          {sim.allocation.simType}
        </Tag>
      );
    }
    return parts.length > 0 ? <>{parts}</> : '\u2014';
  };

  return (
    <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: 12 } }}>
      {/* Top row: checkbox, provider, identifier, kebab */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Checkbox
          checked={selected}
          disabled={selectDisabled}
          onChange={e => onSelectChange(e.target.checked)}
          aria-label={`Select SIM ${sim.identifier}`}
        />
        <Tag color={providerColor(sim.provider)} style={{ margin: 0 }}>
          {providerLabel(sim.provider)}
        </Tag>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 500, wordBreak: 'break-all' }}>
          {sim.provider === 'vrs' ? (
            <Link to={`/admin/sims/${sim.provider}/${sim.identifier}`}>{sim.identifier}</Link>
          ) : (
            sim.identifier
          )}
        </div>
        {menuItems.length > 0 && (
          <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
              aria-label={`Actions for SIM ${sim.identifier}`}
              style={{ flexShrink: 0 }}
            />
          </Dropdown>
        )}
      </div>

      {/* Body: label/value rows */}
      <FieldRow label="MSISDN">{sim.msisdn || '\u2014'}</FieldRow>
      <FieldRow label="Status">
        <Tag color={statusColor(sim.status)} style={{ margin: 0 }}>
          {sim.status}
        </Tag>
      </FieldRow>
      <FieldRow label="Alias">{sim.alias || '\u2014'}</FieldRow>
      <FieldRow label="Allocated To">{sim.allocation ? sim.allocation.orgName : '\u2014'}</FieldRow>
      <FieldRow label="Assigned To">{renderAssignedTo()}</FieldRow>
    </Card>
  );
};

export default SimListCard;
